import { Cell, Grid } from '@fs/zion-ui'
import { useHistory } from '@fs/zion-router'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { ThingFlag } from '@fs/zion-icon'
import { dmcRed, marginBottom, marginTop, redFlagStyle } from '../../util/CssUtil'
import useUser from '../../hooks/useUser'
import { PermissionsContext } from '../app/PermissionsContext'
import SubnavAndRoutes from '../app/SubnavAndRoutes'
import ArtifactsBase from '../artifacts/ArtifactsBase'
import MetaData from '../metadata/MetaData'
import RelationshipCalculatorProvider, {
  initialRelCalculatorContext,
} from '../relationshipCalculator/RelationshipCalculatorContext'
import UserContributions from './contributions/UserContributions'
import UserGroups from './groups/UserGroups'
import PrivatePersons from './privatePersons/PrivatePersons'
import UserReservations from './reservations/UserReservations'
import UserContextProvider from './UserContext'
import UserCtr from './ctr/UserCtr'
import UserCompletedOrdinances from './ordinances/UserCompletedOrdinances'
import UserRelationshipCalculator from './relationshipCalculator/UserRelationshipCalculator'
import UserSummary from './summary/UserSummary'
import { isNullOrUndefinedOrEmpty } from '../../util/Utils'
import { UserMessages } from './messages/UserMessages'
import NewUserMessages from './messages/NewUserMessages'
import UserGedcom from './gedcom/UserGedcom'
import CmisReferences from '../common/cmisrefs/CmisReferences'
import UserTrees from './tree/UserTrees'
import { usePageTitle } from '../app/PageTitleContext'
import { getOwnedTreesForUser, getParticipantTreesForUser } from '../../services/userService'
import useNotifications from '../../hooks/useNotifications'

const mb10 = marginBottom('10px')
const mt10 = marginTop('10px')
const redFlag = () => <ThingFlag size="xs" color={dmcRed} />

export default function UserBase({ match }) {
  usePageView({ page_detail: 'DMConsole:UserBase' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.base'))
  const { isRole } = useContext(PermissionsContext)
  const userId = match.params.userId
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const forward = searchParams.get('forward')
  const context = searchParams.get('context')
  const contextParam = context ? `?context=${context}` : ''
  const [currentUser, fetchCurrentUser] = useUser(userId, context, forward)
  const [ownedTrees, setOwnedTrees] = useState([])
  const [participantTrees, setParticipantTrees] = useState([])
  const { error } = useNotifications()

  const cisId = currentUser?.dmcUser?.cisId

  useEffect(() => {
    if (!cisId) return
    getOwnedTreesForUser(cisId)
      .then((response) => setOwnedTrees(response.data))
      .catch((err) =>
        error(
          t('user.tree.errors.getOwnedTrees', 'Error getting trees owned by this user - {message}', {
            message: err.message || '',
          })
        )
      )
    getParticipantTreesForUser(cisId)
      .then((response) => setParticipantTrees(response.data))
      .catch((err) =>
        error(
          t('user.tree.errors.getParticipantTrees', 'Error getting trees that this user participates in - {message}', {
            message: err.message || '',
          })
        )
      )
  }, [cisId, error, t])

  if (isNullOrUndefinedOrEmpty(currentUser) && userId == null) {
    return <>{t('user.lookup', 'Look up a user to view user data.')}</>
  }

  //  When coming from a link don't display the message above
  if (isNullOrUndefinedOrEmpty(currentUser) && userId != null) {
    return null
  }

  const hasTrees = ownedTrees.length > 0 || participantTrees.length > 0
  const treesStyle = hasTrees ? redFlagStyle : null
  const treesFlag = hasTrees ? redFlag() : ''

  const subNavs = [
    {
      text: t('summary', 'Summary'),
      path: `summary${contextParam}`,
      requiredPermissions: 'FtLoginPermission',
      component: UserSummary,
    },
    {
      text: t('reservations.label', 'Reservations'),
      path: `reservations${contextParam}`,
      requiredPermissions: 'FtLoginPermission',
      component: UserReservations,
    },
    {
      text: t('user.subnav.completedOrdinances', 'Completed Ordinances'),
      path: `ordinances${contextParam}`,
      requiredPermissions: 'FtLoginPermission',
      component: UserCompletedOrdinances,
    },
    {
      text: t('relationshipCalculator.label', 'Relationship Calculator'),
      path: `relationshipcalculator${contextParam}`,
      requiredPermissions: 'FtLoginPermission',
      component: UserRelationshipCalculator,
    },
    {
      text: t('references', 'References'),
      path: `cmisreferences${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: CmisReferences,
    },
    {
      text: t('user.subnav.contributions', 'Contributions'),
      path: `contributions${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: UserContributions,
    },
    {
      text: t('ctr.label', 'CTR'),
      path: `ctr${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: UserCtr,
    },
    {
      text: t('artifacts.label', 'Artifacts'),
      path: `artifacts/patron/${currentUser?.dmcUser?.cisId}`,
      requiredPermissions: ['FtLoginPermission'],
      component: ArtifactsBase,
      nonParentUrl: true,
    },
    {
      text: t('user.subnav.privatePersons', 'Private Persons'),
      path: `privatePersons${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: PrivatePersons,
    },
  ]

  subNavs.push({
    text: (
      <span>
        <span style={treesStyle}>{t('user.subnav.trees', 'Trees')}</span>
        {treesFlag}
      </span>
    ),
    path: `trees${contextParam}`,
    requiredPermissions: ['FtLoginPermission'],
    component: UserTrees,
  })

  if (isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    subNavs.push({
      text: t('messages.label', 'New Messages'),
      path: `messages${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: NewUserMessages,
    })
  }

  if (isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    subNavs.push({
      text: t('messages.oldLabel', 'Old Messages'),
      path: `oldmessages/${currentUser?.dmcUser?.cisId}${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: UserMessages,
    })
  }

  if (isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    subNavs.push({
      text: t('user.subnav.groups', 'Groups'),
      path: `groups${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: UserGroups,
    })
  }
  if (isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])) {
    subNavs.push({
      text: t('user.subnav.gedcom', 'GEDCOM'),
      path: `gedcom${contextParam}`,
      requiredPermissions: ['FtLoginPermission'],
      component: UserGedcom,
    })
  }
  return (
    <div css={mb10}>
      <UserContextProvider value={{ currentUser, fetchCurrentUser, ownedTrees, participantTrees }}>
        <RelationshipCalculatorProvider value={initialRelCalculatorContext}>
          <Grid>
            <Cell column={12}>
              <MetaData metadata={currentUser.metadata} />
              <SubnavAndRoutes data={subNavs} forward={forward} css={mt10} />
            </Cell>
          </Grid>
        </RelationshipCalculatorProvider>
      </UserContextProvider>
    </div>
  )
}
